import moment from 'moment';
import { FaLock } from 'react-icons/fa6';
import { GiCheckMark } from 'react-icons/gi';
import { LuLogIn } from 'react-icons/lu';
import { RxCross2 } from 'react-icons/rx';
import { TfiReload } from 'react-icons/tfi';
import { RegistrationRequest } from '../../types/applications';
import AcademiaLinkButton from '../CommonComps/Buttons/AcademiaLinkButton';
import SectionHeader from '../CommonComps/Titles/SectionHeader';

const ApplicationProgressTracker = ({
  registrationRequest,
}: {
  registrationRequest: RegistrationRequest;
}) => {
  // destructuring the registration request object here
  const { is_view, status, comments, is_resubmit } = registrationRequest || {};

  // rendering application progress tracker section component here
  return (
    <section>
      <SectionHeader headerText='Application Progress Tracking' />

      <div className='lg:w-4/5 mx-auto bg-base-white rounded-xl shadow-academia-sm px-5 pt-5 pb-3 md:px-8 md:pt-8 md:pb-5'>
        <div>
          <div className='flex items-center'>
            <div className='flex flex-col items-center justify-center gap-2'>
              <div className='relative size-8 flex items-center justify-center rounded-full bg-base-cyan text-base-white'>
                <GiCheckMark className='relative z-10 size-5' />
                {!is_view ? (
                  <div className='absolute size-8 rounded-full bg-base-cyan animate-ping'></div>
                ) : null}
              </div>
            </div>

            <div
              className={`h-1 flex-1 ${
                is_view
                  ? 'bg-gradient-to-r from-base-cyan to-base-purple'
                  : 'bg-gray-200'
              } `}
            ></div>

            <div className='flex flex-col items-center justify-center gap-2'>
              <div
                className={`relative size-8 flex items-center justify-center rounded-full ${
                  is_view
                    ? 'bg-base-purple text-base-white'
                    : 'bg-gray-200 text-gray-400'
                }`}
              >
                {is_view ? (
                  <GiCheckMark className='relative z-10 size-5' />
                ) : (
                  <FaLock className='relative z-10 size-[18px]' />
                )}

                {is_view ? (
                  status === 'approved' ? null : status ===
                    'rejected' ? null : status === 'resubmit' &&
                    !is_resubmit ? null : (
                    <div className='absolute size-8 rounded-full bg-base-purple animate-ping'></div>
                  )
                ) : null}
              </div>
            </div>

            <div
              className={`h-1 flex-1 ${
                status === 'approved'
                  ? 'bg-gradient-to-r from-base-purple to-base-dark-green'
                  : status === 'rejected'
                  ? 'bg-gradient-to-r from-base-purple to-base-red'
                  : status === 'resubmit' && !is_resubmit
                  ? 'bg-gradient-to-r from-base-purple to-base-orange'
                  : 'bg-gray-200'
              }`}
            ></div>

            <div className='flex flex-col items-center justify-center gap-2'>
              <div
                className={`relative size-8 flex items-center justify-center rounded-full  ${
                  status === 'approved'
                    ? 'bg-base-dark-green text-base-white'
                    : status === 'rejected'
                    ? 'bg-base-red text-base-white'
                    : status === 'resubmit' && !is_resubmit
                    ? 'bg-base-orange text-base-white'
                    : 'bg-gray-200 text-gray-400'
                }`}
              >
                {status === 'approved' ? (
                  <GiCheckMark className='relative z-10 size-5' />
                ) : status === 'rejected' ? (
                  <RxCross2 className='relative z-10 size-5' />
                ) : status === 'resubmit' && !is_resubmit ? (
                  <TfiReload className='relative z-10 size-5' />
                ) : (
                  <FaLock className='relative z-10 size-[18px]' />
                )}

                {status === 'approved' ? (
                  <div className='absolute size-8 rounded-full bg-base-dark-green animate-ping'></div>
                ) : status === 'rejected' ? (
                  <div className='absolute size-8 rounded-full bg-base-red animate-ping'></div>
                ) : status === 'resubmit' && !is_resubmit ? (
                  <div className='absolute size-8 rounded-full bg-base-orange animate-ping'></div>
                ) : null}
              </div>
            </div>
          </div>

          <div className='grid grid-cols-3 mt-2'>
            <p className='text-sm font-medium text-base-cyan text-left'>
              {!is_resubmit
                ? 'Application Submitted'
                : 'Application Resubmitted'}
            </p>

            <p
              className={`text-sm font-medium text-center ${
                is_view ? 'text-base-purple' : 'text-gray-300'
              }`}
            >
              Application Reviewed
            </p>

            {status === 'approved' ? (
              <p className='text-sm font-medium text-base-dark-green text-right'>
                Application Approved
              </p>
            ) : status === 'rejected' ? (
              <p className='text-sm font-medium text-base-red text-right'>
                Application Rejected
              </p>
            ) : status === 'resubmit' && !is_resubmit ? (
              <p className='text-sm font-medium text-base-orange text-right'>
                Resubmit Application
              </p>
            ) : (
              <p className='text-sm font-medium text-gray-300 text-right'>
                Application Pending
              </p>
            )}
          </div>
        </div>

        {comments && !is_resubmit ? (
          <div className='mt-5 md:mt-8'>
            <h3 className='text-xl font-medium text-base-cyan'>Comments</h3>
            <div className='border-2 border-base-cyan rounded-lg px-3 py-5'>
              <p className='text-base-dark-blue'>{comments}</p>
            </div>

            <p className='mt-2 text-xs text-center md:text-right text-base-dark-blue'>
              Reviewed on{' '}
              <span className='font-medium text-base-light-blue'>
                {moment(registrationRequest.reviewed_at).format('DD-MM-YYYY')}
              </span>{' '}
              at{' '}
              <span className='font-medium text-base-light-blue'>
                {moment(registrationRequest.reviewed_at).format('hh:mm A')}
              </span>
            </p>
          </div>
        ) : null}
      </div>

      {status === 'approved' ? (
        <div className='mt-5 w-full md:w-3/5 mx-auto'>
          <AcademiaLinkButton href='/dashboard' variant='premium'>
            <LuLogIn className='size-5' />
            <span>Goto Dashboard</span>
          </AcademiaLinkButton>
        </div>
      ) : null}
    </section>
  );
};

export default ApplicationProgressTracker;
